import escapeHtml from 'escape-html';

import {
  MyCalloutElement,
  MyElement,
  MyElementOrText,
  MyEmbedElement,
  MyHtmlElement,
  MyImageElement,
  MyInlineImageElement,
  MyVideoEmbedElement,
  RichText,
} from './plateTypes';
import { Text } from './slate/package';
import type { ArticleStructure } from './structure/types';

const EDITOR_VIEWPORT_WIDTH = 640;

const newLinesToHtmlBr = (html: string): string =>
  html.replaceAll('\n', '<br />');

const idAttr = (node: { id?: string }, includeIds: boolean): string =>
  includeIds && node.id ? ` id="${node.id}"` : '';

const captionText = (caption: RichText[] | undefined): string =>
  caption
    ? caption
        .map((n) => n.text)
        .join('')
        .trim()
    : '';

const PROVIDER_NAMES: Record<string, string | undefined> = {
  youtube: 'YouTube',
  vimeo: 'Vimeo',
  loom: 'Loom',
};

const hostnameOf = (url: string | null): string => {
  if (!url) return '';
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
};

const serialize = (node: MyElementOrText, includeIds: boolean): string => {
  if (Text.isText(node)) {
    let string = newLinesToHtmlBr(escapeHtml(node.text));
    if ('bold' in node) {
      string = `<strong>${string}</strong>`;
    }
    if ('italic' in node) {
      string = `<em>${string}</em>`;
    }
    if ('underline' in node) {
      string = `<u>${string}</u>`;
    }
    if ('strikethrough' in node) {
      string = `<s>${string}</s>`;
    }
    if ('code' in node) {
      string = `<code>${string}</code>`;
    }
    return string;
  }

  if ('text' in node) {
    return '';
  }

  const children: string =
    node.children && node.children.length
      ? node.children.map((n) => serialize(n, includeIds)).join('')
      : '';

  const type = node.type as string;
  const id = idAttr(node as { id?: string }, includeIds);
  const headingId = node.id ? ` id="${node.id}"` : '';

  switch (type) {
    case 'heading_large':
    case 'h_lg':
      return `<h2${headingId}>${children}</h2>`;
    case 'heading_medium':
    case 'h_md':
      return `<h3${headingId}>${children}</h3>`;
    case 'heading_small':
    case 'h_sm':
      return `<h4${headingId}>${children}</h4>`;
    case 'codeblock':
    case 'code_block':
      return `<pre${id} class="code-block">${children.replaceAll(
        '<br />',
        '\n',
      )}</pre>`;
    case 'divider':
    case 'hr':
      return `<hr${id} />`;
    case 'callout':
      return generateCalloutHTML(node as MyCalloutElement, includeIds);
    case 'blockquote':
      return `<blockquote${id}>${children}</blockquote>`;
    case 'ol':
      return `<ol${id}>${children}</ol>`;
    case 'ul':
      return `<ul${id}>${children}</ul>`;
    case 'li':
      return `<li${id}>${children}</li>`;
    case 'lic':
      return children;
    case 'a':
      return `<a href="${(node as { url: string }).url}" target="${
        (node as { target?: string }).target || '_blank'
      }" rel="noopener noreferrer">${children}</a>`;
    case 'interlink': {
      const interlink = node as { linkId?: string; langId?: string };
      const linkId = escapeHtml(interlink.linkId ?? '');
      const langId = escapeHtml(interlink.langId ?? '');
      return `<a class="link-internal" data-knowledge-article="${linkId}" data-lang-id="${langId}">${children}</a>`;
    }
    case 'anchor_link':
      return `<a href="#${(node as { linkId?: string }).linkId}" class="link">${children}</a>`;
    case 'paragraph':
    case 'p':
      return `<p${id}>${children}</p>`;
    case 'table':
      return `<table${id}><tbody>${children}</tbody></table>`;
    case 'tr':
      return `<tr${id}>${children}</tr>`;
    case 'th':
      return `<th${id}>${children}</th>`;
    case 'td':
      return `<td${id}>${children}</td>`;
    case 'img':
      return generateImageHTML(node as MyImageElement, includeIds);
    case 'inline_img':
      return generateInlineImageHTML(node as MyInlineImageElement);
    case 'video_embed':
      return generateVideoEmbedHTML(node as MyVideoEmbedElement, includeIds);
    case 'embed':
      return generateEmbedHTML(node as MyEmbedElement, includeIds);
    case 'html':
      return generateHtmlEmbedHTML(node as MyHtmlElement, includeIds);
    default:
      return '';
  }
};

function generateCalloutHTML(
  node: MyCalloutElement,
  includeIds: boolean,
): string {
  const children: string =
    node.children && node.children.length
      ? node.children.map((n) => serialize(n, includeIds)).join('')
      : '';
  const id = idAttr(node, includeIds);
  return `<p${id} class="callout${
    node.variety ? ` ${node.variety}` : ''
  }">${children}</p>`;
}

function getAlignClass(align: string): string {
  switch (align) {
    case 'center':
      return 'image-align-center';
    case 'start':
      return 'image-align-start';
    case 'end':
      return 'image-align-end';
    default:
      return 'image-stretch';
  }
}

function getWidthPercent(
  widthPixels: number | undefined,
  align: string | undefined,
): number {
  const width =
    align === 'stretch' || !align || !widthPixels
      ? 0
      : Math.round((widthPixels / EDITOR_VIEWPORT_WIDTH) * 10000) / 100;

  return width;
}

function generateImageHTML(node: MyImageElement, includeIds: boolean): string {
  if (!node.url) return '';

  const caption: string =
    node.caption && node.caption.length
      ? node.caption.map((n) => serialize(n, includeIds)).join('')
      : '';
  const hasCaption = !!caption;
  const linkHtml = node.linkUrl
    ? `<a href="${node.linkUrl}" target="_blank" rel="noopener noreferrer">`
    : '';

  const alignClass = getAlignClass(node.imgAlign || 'center');
  const width = getWidthPercent(node.width, node.imgAlign);
  const style = width ? `style="width: ${width}%;"` : '';
  const id = idAttr(node, includeIds);

  return `${linkHtml}<figure${id} class="image-figure">
<img
  src="${node.url}?width=${EDITOR_VIEWPORT_WIDTH}"
  srcset="
    ${node.url}?width=${Math.round(EDITOR_VIEWPORT_WIDTH / 2)} ${Math.round(EDITOR_VIEWPORT_WIDTH / 2)}w,
    ${node.url}?width=${EDITOR_VIEWPORT_WIDTH} ${EDITOR_VIEWPORT_WIDTH}w,
    ${node.url}?width=${Math.round(EDITOR_VIEWPORT_WIDTH * 1.5)} ${Math.round(EDITOR_VIEWPORT_WIDTH * 1.5)}w,
    ${node.url}?width=${Math.round(EDITOR_VIEWPORT_WIDTH * 2)} ${Math.round(EDITOR_VIEWPORT_WIDTH * 2)}w
  "
  sizes="(min-width: ${EDITOR_VIEWPORT_WIDTH}px) ${EDITOR_VIEWPORT_WIDTH}px, 100%"
  class="image-preview ${alignClass}" ${style} ${
    node.originalWidth ? `width="${node.originalWidth}" ` : ''
  } ${node.originalHeight ? `height="${node.originalHeight}" ` : ''}
    alt="${escapeHtml(captionText(node.caption))}"
    decoding="async"
    loading="lazy"
  >
${
  hasCaption
    ? `<figcaption class="image-caption ${alignClass}">${caption}</figcaption>`
    : ''
}
</figure>${linkHtml ? '</a>' : ''}`;
}

function generateInlineImageHTML(node: MyInlineImageElement): string {
  const heightNum =
    node.originalHeight && node.originalWidth && node.width
      ? (node.width / node.originalWidth) * node.originalHeight
      : undefined;

  const height = heightNum ? `height="${heightNum}"` : '';
  const width = node.width ? `width="${node.width}"` : '';
  const src = node.width ? `${node.url}?width=${node.width}` : node.url;

  return `<img src="${src}" class="image-inline" alt="" ${width} ${height} decoding="async" />`;
}

function generateVideoEmbedHTML(
  node: MyVideoEmbedElement,
  includeIds: boolean,
): string {
  if (!node.url) return '';

  const caption: string =
    node.caption && node.caption.length
      ? node.caption.map((n) => serialize(n, includeIds)).join('')
      : '';
  const hasCaption = !!caption;

  const alignClass = getAlignClass(node.align || 'stretch');
  const width = getWidthPercent(node.width, node.align);
  const style = width ? `style="width: ${width}%;"` : '';
  const providerClass = `video-embed-${node.provider}`;
  const id = idAttr(node, includeIds);
  const videoTitle =
    captionText(node.caption) || PROVIDER_NAMES[node.provider ?? ''] || 'Video';

  return `<figure${id} class="video-figure">
  <div class="video-embed ${alignClass}" ${style}>
    <div class="${providerClass}">
      <iframe src="${
        node.url
      }" title="${escapeHtml(videoTitle)}" class="video-embed-iframe" allowFullScreen></iframe>
    </div>
${
  hasCaption
    ? `<figcaption class="video-caption ${alignClass}">${caption}</figcaption>`
    : ''
}
</div>
</figure>`;
}

function generateEmbedHTML(node: MyEmbedElement, includeIds: boolean): string {
  if (!node.src) return '';

  const paddingBottom = Math.round(
    ((node.height || 480) / EDITOR_VIEWPORT_WIDTH) * 100,
  );

  const style = `style="padding-bottom: ${paddingBottom}%"`;
  const id = idAttr(node, includeIds);
  const embedTitle = node.title || hostnameOf(node.src) || 'Embedded content';
  return `<div${id} class="embed" ${style}>
  <iframe src="${node.src}" title="${escapeHtml(embedTitle)}" class="embed-iframe" allowFullScreen></iframe>
</div>`;
}

function generateHtmlEmbedHTML(
  node: MyHtmlElement,
  includeIds: boolean,
): string {
  if (!node.html || node.html.length === 0) return '';
  const id = idAttr(node, includeIds);
  return `<html-block${id} src="${encodeURIComponent(node.html)}"${
    node.useArticleStyles !== false ? ' use-article-styles="true"' : ''
  }></html-block>`;
}

export function serializeToHtml(
  content: ArticleStructure | null,
  includeIds = false,
): string {
  if (!content) return '';
  return content.children.map((n) => serialize(n, includeIds)).join('');
}

export function serializeNodesToHtml(
  nodes: Array<MyElement>,
  includeIds = false,
): string {
  return nodes.map((n) => serialize(n, includeIds)).join('');
}
